<template>
    <div class="row">
        <div :class="messageCard ? 'col-12 col-lg-8 col-xxl-8' : 'col-12'">
            <div class="card chat-card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center gap-3">
                        <h5 class="card-title mb-0">Messages</h5>
                        <span class="badge bg-primary">{{ totalRows }} messages</span>
                    </div>
                    <div class="d-flex gap-2">
                        <div class="input-group" style="width: 300px;">
                            <span class="input-group-text bg-white border-end-0">
                                <i class="align-middle" data-feather="search"></i>
                            </span>
                            <input type="text" class="form-control border-start-0" placeholder="Rechercher..."
                                v-model="searchQuery">
                        </div>
                        <button class="btn btn-primary" @click="setShowMessageCard(true)" v-if="!messageCard">
                            <i class="align-middle me-2" data-feather="plus"></i>
                            Nouveau message
                        </button>
                    </div>
                </div>

                <div class="card-body p-0">
                    <!-- Loading State -->
                    <div v-if="isLoading" class="d-flex flex-column justify-content-center align-items-center"
                        style="height: 500px;">
                        <span class="loader mb-3"></span>
                        <b>Chargement en cours...</b>
                    </div>

                    <!-- Chat Messages -->
                    <div v-else class="chat-container" ref="chatContainer">
                        <div v-if="filteredMessages.length === 0" class="text-center py-5 text-muted">
                            <i class="align-middle" data-feather="inbox" style="width: 48px; height: 48px;"></i>
                            <p class="mt-3">Aucun message trouvé</p>
                        </div>

                        <div v-for="(message, i) in filteredMessages" :key="i"
                            :class="['chat-message', message.sender_name === 'SYSTEME' ? 'sent' : 'received']">

                            <!-- Avatar for received messages -->
                            <div class="message-avatar" v-if="message.sender_name !== 'SYSTEME'">
                                <div class="avatar">
                                    {{ getInitials(message.sender_name) }}
                                </div>
                            </div>

                            <!-- Message Bubble -->
                            <div class="message-bubble-container">
                                <div class="message-bubble">
                                    <!-- Sender Info -->
                                    <div class="message-sender">
                                        <strong v-if="message.sender_name !== 'SYSTEME'">{{ message.sender_name }}</strong>
                                        <strong v-else >{{ message.sender_imei }}</strong>
                                        <span class="imei-badge" v-if="message.sender_imei">{{ message.sender_imei }}</span>
                                    </div>

                                    <!-- Receiver Info -->

                                    <div class="message-receiver">
                                        <i class="align-middle" data-feather="arrow-right"></i>

                                        <!-- Nom du receveur -->
                                        <span class="truncate" :title="message.receiver_name">
                                            {{ message.receiver_name }}
                                        </span>

                                        <!-- IMEI -->
                                        <span class="truncate imei-badge" :title="message.receiver_imei">
                                            {{ message.receiver_imei }}
                                        </span>
                                    </div>

                                    <!-- <div class="message-receiver">
                                        <i class="align-middle" data-feather="arrow-right"></i>
                                        <span>{{ message.receiver_name }}</span>
                                        <span class="imei-badge">{{ message.receiver_imei }}</span>
                                    </div> -->

                                    <!-- Message Text -->
                                    <div class="message-text">
                                        {{ message.message }}
                                    </div>

                                    <!-- Message Footer -->
                                    <div class="message-footer">
                                        <h2 class="message-time">
                                            <i class="align-middle" data-feather="clock"></i>
                                            {{ formatDate(message.sender_date) }}
                                        </h2>
                                        <!-- <MessageCode :code="message.status" /> -->
                                    </div>
                                </div>
                            </div>

                            <!-- Avatar for sent messages -->
                            <div class="message-avatar" v-if="message.sender_name === 'SYSTEME'">
                                <div class="avatar avatar-system">
                                    {{ getInitials(message.sender_imei) }}
                                </div>
                            </div>
                        </div>

                        <!-- Scroll to bottom button -->
                        <transition name="fade">
                            <button v-if="showScrollButton" class="scroll-to-bottom" @click="scrollToBottom"
                                title="Aller en bas">
                                &#x2193;
                            </button>
                        </transition>
                    </div>
                </div>
            </div>
        </div>

        <!-- Message Form -->
        <div class="col-12 col-lg-4 col-xxl-4" v-if="messageCard">
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                        <i class="align-middle me-2" data-feather="send"></i>
                        Nouveau message
                    </h5>
                    <button type="button" class="btn-close" @click="setShowMessageCard(false)"></button>
                </div>

                <form class="card-body">
                    <!-- Destinataires Multi-Select -->
                    <div class="mb-3">
                        <label class="form-label">
                            Destinataire(s) <span class="text-danger">*</span>
                        </label>
                        <MultiSelect v-model="messageContent.receiver_imei" :options="formattedDevices" filter
                            optionLabel="displayName" placeholder="Sélectionner des destinataires"
                            :maxSelectedLabels="3" class="w-100"
                            :class="{ 'is-invalid': messageContent.receiver_imei.length === 0 && formSubmitted }" />
                        <small v-if="messageContent.receiver_imei.length === 0 && formSubmitted" class="text-danger">
                            Veuillez sélectionner au moins un destinataire.
                        </small>
                        <small v-else class="text-muted">
                            {{ messageContent.receiver_imei.length }} destinataire(s) sélectionné(s)
                        </small>
                    </div>

                    <!-- Saisie rapide IMEI -->
                    <!-- <div class="mb-3">
                        <label class="form-label">
                            Ou saisir des IMEI directement
                            <small class="text-muted">(séparés par des virgules)</small>
                        </label>
                        <div class="input-group">
                            <span class="input-group-text">@</span>
                            <input type="text" class="form-control" v-model="quickImeiInput"
                                placeholder="Ex: @301434032307940,@301434032307941" @blur="parseQuickImei">
                        </div>
                        <small class="text-muted">
                            Utilisez @ suivi de l'IMEI, séparés par des virgules
                        </small>
                    </div> -->

                    <!-- Message -->
                    <div class="mb-3">
                        <label class="form-label">
                            Message <span class="text-danger">*</span>
                            <small class="text-muted">(max 160 caractères)</small>
                        </label>
                        <textarea v-model="messageContent.message" class="form-control" rows="5" maxlength="160"
                            placeholder="Écrivez votre message ici..."
                            :class="{ 'is-invalid': messageContent.message.length === 0 && formSubmitted }"></textarea>
                        <div class="d-flex justify-content-between mt-1">
                            <small v-if="messageContent.message.length === 0 && formSubmitted" class="text-danger">
                                Ce champ est obligatoire.
                            </small>
                            <small v-else>&nbsp;</small>
                            <small :class="messageContent.message.length >= 160 ? 'text-danger' : 'text-muted'">
                                {{ messageContent.message.length }} / 160 caractères
                            </small>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="d-flex gap-2">
                        <button v-if="!isLoading" type="button" class="btn btn-primary flex-grow-1"
                            @click="sendMessage(messageContent)">
                            <i class="align-middle me-2" data-feather="send"></i>
                            Envoyer
                        </button>
                        <button v-else type="button" class="btn btn-primary flex-grow-1" disabled>
                            <span class="spinner-border spinner-border-sm me-2"></span>
                            Envoi en cours...
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="resetForm">
                            Réinitialiser
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapActions } from 'vuex';
import MultiSelect from 'primevue/multiselect';
import fetch from '@/utils/fetch';
import { useToast } from 'vue-toastification';
import * as types from '@/store/modules/messages/list/mutation_types';
import store from '@/store';
// import MessageCode from './widgets/MessageCode.vue';

export default {
    name: "MessageContent",
    components: {
        MultiSelect,
        // MessageCode,
    },
    data() {
        return {
            messageContent: {
                sender_imei: 'SYSTEME',
                sender_name: "SYSTEME",
                receiver_imei: [],
                message: '',
            },
            messageCard: true,
            devices: [],
            formSubmitted: false,
            searchQuery: '',
            quickImeiInput: '',
            selectedMessage: null,
            showScrollButton: false,
            refreshInterval: null
        };
    },
    computed: {
        ...mapFields('messages/list', {
            messages: 'items',
            currentPage: 'currentPage',
            perPage: 'perPage',
            totalRows: "totalRows",
            isLoading: 'isLoading',
        }),
        filteredMessages() {
            if (!this.searchQuery) return this.messages;

            const query = this.searchQuery.toLowerCase();
            return this.messages.filter(msg =>
                msg.sender_name?.toLowerCase().includes(query) ||
                msg.receiver_name?.toLowerCase().includes(query) ||
                msg.sender_imei?.toLowerCase().includes(query) ||
                msg.receiver_imei?.toLowerCase().includes(query) ||
                msg.message?.toLowerCase().includes(query)
            );
        },
        formattedDevices() {
            return this.devices.map(device => ({
                ...device,
                displayName: `${device.name} (${device.imei})`
            }));
        }
    },
    watch: {
        messages: {
            handler() {
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            },
            deep: true
        }
    },
    mounted() {
        this.fetchData();
        this.getAllDeviceLite();
        this.$nextTick(() => {
            this.scrollToBottom();
            this.setupScrollListener();
        });

        this.refreshInterval = setInterval(() => {
            this.fetchData();
        }, 300000);
    },
    beforeUnmount() {
        if (this.$refs.chatContainer) {
            this.$refs.chatContainer.removeEventListener('scroll', this.handleScroll);
        }
        clearInterval(this.refreshInterval);
    },
    methods: {
        ...mapActions({
            // getMessages: "messages/list/getAllMessages",
            getMessages: "messages/list/getMessages",

        }),
        setShowMessageCard(value) {
            this.messageCard = value;
            if (value) {
                this.resetForm();
            }
        },
        fetchData() {
            // SEARCH_ALL_LAST
            this.getMessages({ tyep: "MAPS_LIST_GET_SEARCH_ALL_LAST", data: null });
        },
        getAllDeviceLite() {
            fetch({ method: 'GET', url: 'device-lite' })
                .then(({ data }) => {
                    this.devices = data['data'];
                })
                .catch((e) => {
                    console.error('Erreur lors du chargement des appareils', e);
                });
        },
        parseQuickImei() {
            if (!this.quickImeiInput) return;

            const imeis = this.quickImeiInput
                .split(',')
                .map(imei => imei.trim().replace('@', ''))
                .filter(imei => imei.length > 0);

            imeis.forEach(imei => {
                const device = this.formattedDevices.find(d => d.imei === imei || d.name.includes(imei));
                if (device && !this.messageContent.receiver_imei.find(r => r.imei === device.imei)) {
                    this.messageContent.receiver_imei.push(device);
                }
            });
        },
        sendMessage() {
            this.formSubmitted = true;

            if (this.messageContent.message.length === 0 || this.messageContent.receiver_imei.length === 0) {
                const toast = useToast();
                toast.warning('Veuillez remplir tous les champs obligatoires.');
                return;
            }

            if (this.messageContent.message.length > 160) {
                const toast = useToast();
                toast.warning('Le message ne peut pas dépasser 160 caractères.');
                return;
            }

            store.commit(`messages/list/${types.SET_LOADING}`, true);

            const payload = {
                sender_imei: this.messageContent.sender_imei,
                sender_name: this.messageContent.sender_name,
                receiver_imei: this.messageContent.receiver_imei.map(device => ({
                    imei: device.imei || device.name
                })),
                message: this.messageContent.message
            };

            fetch({ method: 'POST', url: 'messages', data: payload })
                .then(({ data }) => {
                    const toast = useToast();
                    toast.success('Message envoyé avec succès !');
                    this.resetForm();
                    this.setShowMessageCard(true);
                    this.fetchData();
                    store.commit(`messages/list/${types.SET_LOADING}`, false);
                    return data;
                })
                .catch((e) => {
                    const toast = useToast();
                    toast.error(e?.response?.data?.message || 'Erreur lors de l\'envoi du message');
                    store.commit(`messages/list/${types.SET_LOADING}`, false);
                });
        },
        resetForm() {
            this.messageContent.message = '';
            this.messageContent.receiver_imei = [];
            this.quickImeiInput = '';
            this.formSubmitted = false;
        },
        selectMessage(message) {
            this.selectedMessage = message;
        },
        getInitials(name) {
            if (!name) return 'D';
            return name
                .split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
        scrollToBottom() {
            if (this.$refs.chatContainer) {
                this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight;
            }
        },
        setupScrollListener() {
            if (this.$refs.chatContainer) {
                this.$refs.chatContainer.addEventListener('scroll', this.handleScroll);
            }
        },
        handleScroll() {
            if (!this.$refs.chatContainer) return;

            const container = this.$refs.chatContainer;
            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;

            // Show button if not at bottom (with 100px threshold)
            this.showScrollButton = (scrollHeight - scrollTop - clientHeight) > 100;
        },
        formatDate(dateString) {
            if (!dateString) return '';

            const date = new Date(dateString);
            const now = new Date();
            const diffInMs = now - date;
            const diffInHours = diffInMs / (1000 * 60 * 60);

            // Si moins de 24h, afficher le temps relatif
            if (diffInHours < 24) {
                const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
                if (diffInMinutes < 1) return 'À l\'instant';
                if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
                const hours = Math.floor(diffInMinutes / 60);
                return `Il y a ${hours}h`;
            }

            // Sinon afficher la date
            const options = {
                day: '2-digit',
                month: 'short',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
                hour: '2-digit',
                minute: '2-digit'
            };
            return date.toLocaleDateString('fr-FR', options);
        }
    },
}
</script>

<style scoped>
.chat-container {
    height: 600px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1.5rem;
    background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
    position: relative;
    scroll-behavior: smooth;
}

.chat-message {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    animation: fadeIn 0.3s ease-in;
    margin-bottom: 1rem;
}

.chat-message.sent {
    flex-direction: row-reverse;
}

.message-avatar {
    flex-shrink: 0;
}

.avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.875rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.avatar-system {
    background: white;
    border: 2px solid #e9ecef;
    color: #3498db;
}

.message-bubble-container {
    max-width: 70%;
    display: flex;
    flex-direction: column;
}

.chat-message.sent .message-bubble-container {
    align-items: flex-end;
}

.message-bubble {
    background: white;
    border-radius: 16px;
    padding: 0.875rem 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
    position: relative;
}

.chat-message.sent .message-bubble {
    background: white;
    color: #2c3e50;
    border: 1px solid #e9ecef;
}

.message-bubble::before {
    content: '';
    position: absolute;
    top: 12px;
    width: 0;
    height: 0;
    border-style: solid;
}

.chat-message.received .message-bubble::before {
    left: -8px;
    border-width: 8px 8px 8px 0;
    border-color: transparent white transparent transparent;
}

.chat-message.sent .message-bubble::before {
    right: -8px;
    border-width: 8px 0 8px 8px;
    border-color: transparent transparent transparent white;
}

.message-sender {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
}

.chat-message.sent .message-sender {
    color: #2c3e50;
}

.message-receiver {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
    opacity: 0.85;
}


.truncate {
    max-width: 120px;
    /* Ajuste selon ton design */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    vertical-align: middle;
}


.imei-badge {
    background: rgba(0, 0, 0, 0.08);
    padding: 0.125rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-family: monospace;
}

.chat-message.sent .imei-badge {
    background: rgba(0, 0, 0, 0.08);
    color: #2c3e50;
}

.message-text {
    color: #2c3e50;
    line-height: 1.5;
    font-size: 0.9375rem;
    margin: 0.5rem 0;
    word-wrap: break-word;
}

.chat-message.sent .message-text {
    color: #2c3e50;
}

.message-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.chat-message.sent .message-footer {
    border-top-color: rgba(0, 0, 0, 0.05);
}

.message-time {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 1.1rem;
    color: #6c757d;
}

.scroll-to-bottom {
    position: sticky;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #3498db;
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
}

.scroll-to-bottom:hover {
    background: #2980b9;
    transform: translateX(-50%) scale(1.1);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.loader {
    width: 48px;
    height: 48px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.chat-container::-webkit-scrollbar {
    width: 8px;
}

.chat-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.chat-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
}

.chat-container::-webkit-scrollbar-thumb:hover {
    background: #555;
}

@media (max-width: 768px) {
    .message-bubble-container {
        max-width: 85%;
    }

    .chat-container {
        padding: 1rem;
    }
}
</style>
